import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService, Admin, CreateAdminData, UpdateAdminData } from '@/services/admin.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Layout } from '@/components/Layout';
import { Plus, Edit, Mail, Shield, ShieldOff, MoreVertical, X, Eye, EyeOff } from 'lucide-react';
import { DropdownMenu, DropdownMenuItem } from '@/components/ui/dropdown-menu';

export function AdminManagementPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetEmailModal, setShowResetEmailModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [formData, setFormData] = useState<CreateAdminData>({
    email: '',
    name: '',
    mobile: '',
    password: '',
  });

  const queryClient = useQueryClient();

  const { data: admins = [], isLoading, refetch } = useQuery({
    queryKey: ['admins'],
    queryFn: async () => {
      const data = await adminService.getAll();
      console.log('Fetched admins:', data);
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: adminService.create,
    onSuccess: async (data) => {
      console.log('Admin created successfully:', data);
      // Invalidate and refetch
      await queryClient.invalidateQueries({ queryKey: ['admins'] });
      const result = await refetch();
      console.log('Refetched admins:', result.data);
      setShowCreateModal(false);
      setFormData({ email: '', name: '', mobile: '', password: '' });
      setShowPassword(false);
    },
    onError: (error: any) => {
      console.error('Error creating admin:', error);
      setSuccessMessage(error.response?.data?.message || 'Failed to create admin');
      setShowSuccessModal(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdminData }) =>
      adminService.update(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admins'] });
      await refetch();
      setShowEditModal(false);
      setSelectedAdmin(null);
    },
    onError: (error: any) => {
      console.error('Error updating admin:', error);
      setSuccessMessage(error.response?.data?.message || 'Failed to update admin');
      setShowSuccessModal(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: adminService.delete,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admins'] });
      await refetch();
      setSuccessMessage('Admin disabled successfully');
      setShowSuccessModal(true);
    },
    onError: (error: any) => {
      console.error('Error disabling admin:', error);
      // Handle 401 errors specifically - don't redirect, show error in modal
      if (error.response?.status === 401) {
        setSuccessMessage('Your session has expired. Please log in again.');
        setShowSuccessModal(true);
        // Don't redirect here - let the user see the error message
        return;
      }
      setSuccessMessage(error.response?.data?.message || 'Failed to disable admin');
      setShowSuccessModal(true);
    },
  });

  const sendResetEmailMutation = useMutation({
    mutationFn: adminService.sendPasswordResetEmail,
    onSuccess: () => {
      setShowResetEmailModal(false);
      setSelectedAdmin(null);
      setSuccessMessage('Password reset email sent successfully');
      setShowSuccessModal(true);
    },
    onError: (error: any) => {
      console.error('Error sending reset email:', error);
      setSuccessMessage(error.response?.data?.message || 'Failed to send password reset email');
      setShowSuccessModal(true);
    },
  });

  const handleCreate = () => {
    createMutation.mutate(formData);
  };

  const handleEdit = (admin: Admin) => {
    setSelectedAdmin(admin);
    setFormData({
      email: admin.email,
      name: admin.name,
      mobile: admin.mobile || '',
      password: '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = () => {
    if (!selectedAdmin) return;
    const updateData: UpdateAdminData = {
      email: formData.email,
      name: formData.name,
      mobile: formData.mobile,
    };
    updateMutation.mutate({ id: selectedAdmin.id, data: updateData });
  };


  const handleSendResetEmail = (admin: Admin) => {
    setSelectedAdmin(admin);
    setShowResetEmailModal(true);
  };

  const confirmSendResetEmail = () => {
    if (!selectedAdmin) return;
    sendResetEmailMutation.mutate(selectedAdmin.email);
  };

  const handleEnable = (admin: Admin) => {
    if (admin.isSuperAdmin) {
      setSuccessMessage('Cannot change super admin status');
      setShowSuccessModal(true);
      return;
    }
    if (admin.status === 'active') {
      setSuccessMessage('Admin is already active');
      setShowSuccessModal(true);
      return;
    }
    updateMutation.mutate({
      id: admin.id,
      data: { status: 'active' },
    });
  };

  const handleDisable = (admin: Admin) => {
    if (admin.isSuperAdmin) {
      setSuccessMessage('Cannot disable super admin');
      setShowSuccessModal(true);
      return;
    }
    if (admin.status === 'inactive') {
      setSuccessMessage('Admin is already disabled');
      setShowSuccessModal(true);
      return;
    }
    setConfirmMessage(`Are you sure you want to disable ${admin.name}?`);
    setConfirmCallback(() => () => {
      deleteMutation.mutate(admin.id);
      setShowConfirmModal(false);
      setConfirmCallback(null);
    });
    setShowConfirmModal(true);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Admin Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground mt-1">
              Manage admin accounts and permissions
            </p>
          </div>
          <Button 
            onClick={() => {
              setShowCreateModal(true);
              setShowPassword(false);
            }} 
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Admin
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Admins</CardTitle>
            <CardDescription>List of all admin accounts</CardDescription>
          </CardHeader>
          <CardContent>
            {admins.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No admins found. The seeded super admin should appear here.</p>
                <p className="text-sm mt-2">Make sure the backend server has run the seed script.</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">Mobile</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Role</th>
                      <th className="text-left p-2">Created</th>
                      <th className="text-left p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((admin) => (
                    <tr key={admin.id} className="border-b">
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          {admin.name}
                          {admin.isSuperAdmin && (
                            <Shield className="h-4 w-4 text-primary" />
                          )}
                        </div>
                      </td>
                      <td className="p-2">{admin.email}</td>
                      <td className="p-2">{admin.mobile || '—'}</td>
                      <td className="p-2">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            admin.status === 'active'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}
                        >
                          {admin.status}
                        </span>
                      </td>
                      <td className="p-2 capitalize">{admin.role}</td>
                      <td className="p-2 text-sm text-muted-foreground">
                        {admin.createdAt
                          ? new Date(admin.createdAt).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="p-2 relative">
                        <div className="flex justify-center">
                          <DropdownMenu
                            trigger={
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            }
                          >
                          <DropdownMenuItem
                            onClick={() => handleEdit(admin)}
                            disabled={admin.isSuperAdmin}
                          >
                            <Edit className="h-4 w-4" />
                            <span>Edit</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleSendResetEmail(admin)}
                          >
                            <Mail className="h-4 w-4" />
                            <span>Send Password Reset Link</span>
                          </DropdownMenuItem>
                          {admin.status === 'active' ? (
                            <DropdownMenuItem
                              onClick={() => handleDisable(admin)}
                              disabled={admin.isSuperAdmin}
                              className="text-destructive"
                            >
                              <ShieldOff className="h-4 w-4" />
                              <span>Disable</span>
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleEnable(admin)}
                              disabled={admin.isSuperAdmin}
                              className="text-primary"
                            >
                              <Shield className="h-4 w-4" />
                              <span>Enable</span>
                            </DropdownMenuItem>
                          )}
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowCreateModal(false);
            setShowPassword(false);
          }}
        >
          <Card 
            className="w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="relative">
              <CardTitle>Add New Admin</CardTitle>
              <CardDescription>Create a new admin account</CardDescription>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowPassword(false);
                }}
                className="absolute top-4 right-4 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <Input
                placeholder="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <Input
                placeholder="Mobile (optional)"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
              <div className="relative">
                <Input
                  placeholder="Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowPassword(false);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="flex-1"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedAdmin && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowEditModal(false);
            setSelectedAdmin(null);
          }}
        >
          <Card 
            className="w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="relative">
              <CardTitle>Edit Admin</CardTitle>
              <CardDescription>Update admin details</CardDescription>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedAdmin(null);
                }}
                className="absolute top-4 right-4 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <Input
                placeholder="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <Input
                placeholder="Mobile (optional)"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedAdmin(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdate}
                  disabled={updateMutation.isPending}
                  className="flex-1"
                >
                  {updateMutation.isPending ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Email Modal */}
      {showResetEmailModal && selectedAdmin && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowResetEmailModal(false);
            setSelectedAdmin(null);
          }}
        >
          <Card 
            className="w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="relative">
              <CardTitle>Send Password Reset Email</CardTitle>
              <CardDescription>
                Send a password reset email to {selectedAdmin.email}
              </CardDescription>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowResetEmailModal(false);
                  setSelectedAdmin(null);
                }}
                className="absolute top-4 right-4 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                A password reset link will be sent to {selectedAdmin.email}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResetEmailModal(false);
                    setSelectedAdmin(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmSendResetEmail}
                  disabled={sendResetEmailMutation.isPending}
                  className="flex-1"
                >
                  {sendResetEmailMutation.isPending ? 'Sending...' : 'Send Email'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Success/Error Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Notification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {successMessage}
              </p>
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccessMessage('');
                  }}
                >
                  OK
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Confirm Action</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {confirmMessage}
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setConfirmMessage('');
                    setConfirmCallback(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (confirmCallback) {
                      confirmCallback();
                    }
                  }}
                >
                  Confirm
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
}

